import {Member} from "@utility/member";
import {EventHandler} from "@utility/eventHandler";
import {GameEvent} from "@game/gameEvent";

export type Modifier = (text: string, parameters: any) => string

const defaultLanguage = "en-GB";
const files = [
    "text"
];

const Modifiers: Record<string, Record<string, Modifier>> = {
    default: {
        article: (text: string) =>
            ["a", "e", "i", "o", "u"].includes(text.charAt(0)) ? "an" : "a",
        commasAnd: (text: string) => {
            const index = text.lastIndexOf(",");

            if (index >= 0) {
                return text.substring(0, index) + " and" + text.substring(index + 1)
            }
            return text;
        },
        lowerCase: (text: string) =>
            text.toLowerCase(),
        possessive: (text: string) =>
            text.charAt(text.length - 1) === "s" ? `${text}'` : `${text}'s`,
        sentenceCase: (text: string) =>
            text.replace(/^\w/, char => char.toUpperCase()),
        text: (text: string, parameters: any) =>
            Localisation.GetText(text, parameters),
        upperCase: (text: string) =>
            text.toUpperCase()
    }
}

export class Localisation {
    private static _catalog: Record<string, string> = {};
    private static modifiers: Record<string, Modifier> = {};
    private static _initialised = false;
    private static readonly _events = new EventHandler<GameEvent>();

    static get events() {
        return this._events;
    }

    static get initialised() {
        return this._initialised;
    }

    static Initialise(language: string) {
        return Promise.all(files.map(file => new Promise<void>(async (resolve, reject) => {
            let catalog: Record<string, string> = {};

            try {
                catalog = await import(`@data/localisation/${language}/${file}.json`);
            }
            catch(exception: any) {
                try {
                    catalog = await import(`@data/localisation/${defaultLanguage}/${file}.json`);
                }
                catch(exception: any) {
                    console.error(exception.message);
                    reject();
                }
            }
            this._initialised = catalog !== undefined;
            this._events.update(new GameEvent("Initialised"));
            Localisation._catalog = {...Localisation._catalog, ...catalog};
            resolve();
            Localisation.modifiers = Modifiers[language] || Modifiers.default;
        })));
    }

    static get catalog() {
        return Localisation._catalog;
    }

    static GetRawText(key: string) {
        return Localisation._catalog[key] || key;
    }

    static GetText(key?: string, parameters: any = {}) {
        if (key === undefined) {
            console.error("Requesting localised undefined string");
            return Localisation._catalog.DebugUndefined;
        }

        let text = Localisation.GetRawText(key);
        const tokens = Object.fromEntries(Array.from((text.matchAll(/\{([^}]+?)}/g) || []))
            .map(matches => ([matches[0], Member.ParseToken(matches[1])])));

        Object.entries(tokens).forEach(([token, member]) => {
            text = text.replace(token, Localisation.GetText(member.getText(parameters, Localisation.modifiers)))
        });
        return text;
    }
}

Localisation.Initialise(navigator.language).then(() =>
    console.log(`Initialised localisation with language ${navigator.language}`)
);
